// src/components/Advertisers.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLockBodyScroll } from '../hooks/useLockBodyScroll';
import ConfirmDialog from './ConfirmDialog';

function Advertisers() {
  const [advertisers, setAdvertisers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAdvertiser, setEditingAdvertiser] = useState(null);
  const [formError, setFormError] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, id: null });

  // Form State - Renamed domain to campaign_template
  const [formData, setFormData] = useState({
    name: '',
    campaign_template: '', 
    payout_model: 'CPA',
    default_payout: '',
    postback_url_template: ''
  });

  useLockBodyScroll(showModal);

  useEffect(() => {
    fetchAdvertisers();
  }, []);

  const fetchAdvertisers = async () => {
    try {
      const response = await axios.get('/advertisers');
      setAdvertisers(response.data.advertisers);
    } catch (error) {
      console.error('Failed to fetch advertisers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAdvertiser) {
        await axios.put(`/advertisers/${editingAdvertiser.id}`, formData);
      } else {
        await axios.post('/advertisers', formData);
      }
      setShowModal(false);
      resetForm();
      fetchAdvertisers();
    } catch (error) {
      setFormError(error.response?.data?.error || 'Operation failed');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      campaign_template: '',
      payout_model: 'CPA',
      default_payout: '',
      postback_url_template: ''
    });
    setEditingAdvertiser(null);
  };

  const handleEdit = (advertiser) => {
    setEditingAdvertiser(advertiser);
    setFormData({
      name: advertiser.name,
      campaign_template: advertiser.campaign_template || '',
      payout_model: advertiser.payout_model,
      default_payout: advertiser.default_payout,
      postback_url_template: advertiser.postback_url_template || ''
    });
    setFormError('');
    setShowModal(true);
  };

  const handleDelete = (id) => setConfirmDialog({ open: true, id });
  const performDelete = async () => {
    try {
      await axios.delete(`/advertisers/${confirmDialog.id}`);
      fetchAdvertisers();
    } catch { setFormError('Failed to delete advertiser'); }
    setConfirmDialog({ open: false, id: null });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Advertisers</h1>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          + Add Advertiser
        </button>
      </div>

      {/* Advertisers List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : (
          <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaign Template</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Model</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Default Payout</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {advertisers.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    No advertisers yet. Click "+ Add Advertiser" to create one.
                  </td>
                </tr>
              )}
              {advertisers.map((advertiser) => (
                <tr key={advertiser.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {advertiser.name}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={advertiser.campaign_template}>
                    {advertiser.campaign_template}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {advertiser.payout_model}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ${advertiser.default_payout}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleEdit(advertiser)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(advertiser.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 pt-[env(safe-area-inset-top)]"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90dvh] overflow-y-auto overscroll-contain"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold mb-4">
              {editingAdvertiser ? 'Edit Advertiser' : 'New Advertiser'}
            </h2>
            {formError && (
              <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-800">{formError}</div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Advertiser Name</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Campaign URL Template</label>
                <input
                  type="text"
                  placeholder="https://example.com/offer?aff_id=123&click_id={click_id}"
                  value={formData.campaign_template}
                  onChange={(e) => setFormData({ ...formData, campaign_template: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Used to auto-fill the Offer URL when creating new campaigns.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Payout Model</label>
                  <select
                    value={formData.payout_model}
                    onChange={(e) => setFormData({ ...formData, payout_model: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="CPA">CPA</option>
                    <option value="CPL">CPL</option>
                    <option value="CPS">CPS</option>
                    <option value="RevShare">RevShare</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Default Payout ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.default_payout}
                    onChange={(e) => setFormData({ ...formData, default_payout: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Postback URL Template (For Advertiser)</label>
                <input
                  type="text"
                  placeholder="https://tr.yoursite.com/postback?click_id={sub2}&payout={payout}"
                  value={formData.postback_url_template}
                  onChange={(e) => setFormData({ ...formData, postback_url_template: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Save the format required by this advertiser for your reference.
                </p>
              </div>

              <div className="sticky bottom-0 -mx-6 -mb-6 px-6 py-4 bg-white border-t flex justify-end gap-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="min-h-[44px] px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 active:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="min-h-[44px] px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 active:bg-blue-800"
                >
                  Save Advertiser
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmDialog.open}
        title="Delete advertiser?"
        message="This will also delete all associated campaigns. This cannot be undone."
        confirmLabel="Delete"
        danger
        onConfirm={performDelete}
        onCancel={() => setConfirmDialog({ open: false, id: null })}
      />
    </div>
  );
}

export default Advertisers;